/**
 * wysiwyg-visual-tools/types.ts
 *
 * Tipos e interfaces compartilhados pelo Smart Visual Editor.
 * ─────────────────────────────────────────────────────────────
 * Para adicionar um novo grupo de ferramentas:
 *   1. Criar `plugins/meu-plugin.ts` implementando VtbPlugin
 *   2. Adicionar panel HTML no Blade  (id="vtb-meu-plugin")
 *   3. Registrar em `index.ts`
 *   ─────────────────────────────────────────────────────────
 */

// ─── Contexto do elemento selecionado ────────────────────────────────────────

export interface SmartCtx {
    /** ID do plugin que controla este contexto (ex: 'vtb-badge') */
    type: string;
    /** Nome amigável para exibir no breadcrumb (ex: 'Badge', 'Alerta') */
    label: string;
    /** Elemento "raiz" detectado pelo plugin */
    el: HTMLElement;
    /** Contêiner pai relevante (ex: .badges, .meta, .steps) */
    container?: HTMLElement;
    /** Dados extras livres para uso pelo plugin */
    extra?: Record<string, unknown>;
}

// ─── Contrato do plugin ───────────────────────────────────────────────────────

export interface VtbPlugin {
    /**
     * ID único do plugin — deve corresponder ao id do painel no Blade.
     * Ex: 'vtb-badge', 'vtb-endpoint'
     */
    readonly panelId: string;

    /**
     * Seletores CSS que identificam os elementos raiz deste plugin.
     * Usado por getChildrenContexts para descoberta dinâmica de filhos editáveis.
     * Ex: ['span.badge', '[data-vtb-id="badge"]']
     */
    readonly selectorHints?: string[];

    /**
     * Lista de panelIds com os quais este plugin tem afinidade de contexto.
     * Quando um desses contextos está ativo, as categorias deste plugin são priorizadas no insert.
     * Ex: ['vtb-cover', 'vtb-badges-group'] significa "mostre-me quando Cover ou BadgesGroup estiver ativo"
     */
    readonly contextAffinity?: string[];

    /**
     * Tenta detectar se o target clicado pertence a este plugin.
     * Retorna o SmartCtx completo (com el + container opcionais), ou null.
     * Chamado em cascata até o primeiro match.
     */
    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null;

    /**
     * Ativa o painel e preenche os campos com os valores atuais do elemento.
     * Chamado após detect() retornar não-null.
     */
    activate(ctx: SmartCtx, registry: IVtbRegistry): void;

    /**
     * Registra event listeners dos botões do painel. Chamado uma única vez no init.
     */
    setup(registry: IVtbRegistry): void;
}

// ─── Interface pública do Registry (usada pelos plugins) ─────────────────────

export interface IVtbRegistry {
    /** Elemento raiz do modal */
    readonly modalEl: HTMLElement | null;
    /** Div contenteditable do editor visual */
    readonly visualEditor: HTMLElement | null;
    /** Textarea HTML (sincronizado após cada operação) */
    readonly htmlEditor: HTMLTextAreaElement | null;
    /** Div de preview */
    readonly previewEl: HTMLElement | null;
    /** Contexto atualmente ativo */
    readonly activeCtx: SmartCtx | null;
    /** Pilha de contextos detectados (do mais externo para o mais interno) */
    readonly contextStack: SmartCtx[];

    /** Busca elemento por ID dentro do modal */
    q<T extends HTMLElement = HTMLElement>(id: string): T | null;

    /** Sincroniza visualEditor → htmlEditor e preview */
    sync(): void;

    /** Seleciona programaticamente um elemento (reinicia detecção) */
    selectEl(el: HTMLElement): void;
    getChildrenContexts(parentEl: HTMLElement): SmartCtx[];
    addComponentSelectors(tags: string[]): void;
    setCategories(categories: any[]): void;
    openMiniModal(anchor: HTMLElement): void;

    /** Alterna para um contexto específico da pilha */
    switchToContext(index: number): void;

    /** Limpa seleção atual e volta ao hint */
    clearSelection(): void;

    /** Exibe um painel específico */
    showPanel(id: string): void;

    /** Oculta todos os painéis .vtb-group */
    hideAllPanels(): void;
}

// ─── Utilitários ─────────────────────────────────────────────────────────────

/** Converte rgb(r,g,b) → #rrggbb. Retorna '' se não reconhecer. */
export function rgbToHex(rgb: string): string {
    if (!rgb || rgb.trim() === '') return '';
    if (rgb.startsWith('#')) return rgb;
    const m = rgb.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!m) return '';
    return '#' + [m[1], m[2], m[3]]
        .map(v => parseInt(v).toString(16).padStart(2, '0'))
        .join('');
}

/**
 * Percorre a árvore DOM de `start` até `boundary` (exclusive) procurando
 * o primeiro ancestral que satisfaça `predicate`. Retorna null se não encontrar.
 */
export function closestInside(
    start: HTMLElement,
    boundary: HTMLElement,
    predicate: (el: HTMLElement) => boolean,
): HTMLElement | null {
    let el: HTMLElement | null = start;
    while (el && el !== boundary) {
        if (predicate(el)) return el;
        el = el.parentElement;
    }
    return null;
}

/**
 * Retorna o filho direto de `container` que contenha (ou seja) `el`.
 * Útil para encontrar o item de lista que contém o clique.
 */
export function directChildOf(container: HTMLElement, el: HTMLElement): HTMLElement | null {
    let cur: HTMLElement | null = el;
    while (cur && cur.parentElement !== container) {
        cur = cur.parentElement;
    }
    return cur;
}

/** Move `el` para cima ou baixo entre seus irmãos. */
export function moveSibling(el: HTMLElement, dir: -1 | 1): void {
    const parent = el.parentElement;
    if (!parent) return;
    if (dir === -1) {
        const prev = el.previousElementSibling;
        if (prev) parent.insertBefore(el, prev);
    } else {
        const next = el.nextElementSibling;
        if (next) parent.insertBefore(next, el);
    }
}

/** Renumera elementos `.num` dentro de `container` a partir de 1. */
export function renumberNums(container: HTMLElement): void {
    const items = container.querySelectorAll<HTMLElement>(':scope > li');
    items.forEach((li, i) => {
        const n = li.querySelector('.num');
        if (n) n.textContent = String(i + 1);
    });
}
